/**
 * Display / CFO UI payable helpers, keep in sync with payment writes (same PR):
 * functions/financialDashboardFunctions/claimDepositSettlement.js (canonical)
 */
import { formatCurrency } from "./claimData";
import { CLAIM_STATUS, normalizeClaimWorkflowStatus } from "./claimWorkflow";

const isCreditNoteClaim = (claim = {}) =>
  String(claim?.intakeKind || "").trim().toLowerCase() === "credit_note";

export const AMOUNT_TOLERANCE = 0.02;

const normalizeAmount = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(
    String(value)
      .replace(/[^0-9.-]/g, "")
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : null;
};

export const resolveClaimBaseAmount = (claim = {}) =>
  normalizeAmount(claim.numericAmount) ?? normalizeAmount(claim.totalAmount) ?? 0;

/** Amount shown in lists/dashboards; uses settlement totals for paid/partial claims. */
export const resolveClaimDisplayAmount = (claim = {}) => {
  const baseAmount = resolveClaimBaseAmount(claim);
  const status = normalizeClaimWorkflowStatus(claim.status);
  const settlement = claim.paymentSettlement || {};
  const settlementInvoiceTotal = normalizeAmount(settlement.invoiceTotal);
  const settlementTotalPaid = normalizeAmount(settlement.totalPaid);

  if (status === CLAIM_STATUS.PAID) {
    if (
      claim.supplierCreditRecorded &&
      settlementInvoiceTotal != null &&
      settlementTotalPaid != null &&
      settlementTotalPaid > settlementInvoiceTotal
    ) {
      return settlementInvoiceTotal;
    }
    return (
      settlementTotalPaid ??
      settlementInvoiceTotal ??
      normalizeAmount(claim.invoiceTotalAmount) ??
      baseAmount
    );
  }

  if (status === CLAIM_STATUS.PARTIAL_PAYMENT) {
    return (
      settlementTotalPaid ??
      normalizeAmount(settlement.popAmountApplied) ??
      baseAmount
    );
  }

  return baseAmount;
};

/** Supplier overview header totals: paid / partial settlement only (excludes rejected, pending, etc.). */
export const resolveSupplierOverviewPaidAmount = (claim = {}) => {
  const status = normalizeClaimWorkflowStatus(claim.status);
  if (status !== CLAIM_STATUS.PAID && status !== CLAIM_STATUS.PARTIAL_PAYMENT) {
    return 0;
  }
  return resolveClaimDisplayAmount(claim);
};

export const resolveClaimListAmount = (claim = {}, mode = "pending") =>
  mode === "processed" ? resolveClaimDisplayAmount(claim) : resolveClaimBaseAmount(claim);

export const resolveClaimNumericAmount = (claim = {}) => {
  const numeric = normalizeAmount(claim.numericAmount);
  if (numeric != null && numeric > 0) return numeric;
  return normalizeAmount(claim.totalAmount) || 0;
};

export const getClaimDepositSignals = (claim = {}) => {
  const signals = claim?.depositSignals;
  return signals && typeof signals === "object" ? signals : {};
};

export const isDepositPaymentClaim = (claim = {}) => {
  const signals = getClaimDepositSignals(claim);
  if (signals.requiresDeposit === true) return true;
  if (normalizeAmount(signals.depositAmount) != null) return true;
  if (normalizeAmount(signals.depositPercentage) != null) return true;
  return false;
};

export const getClaimPaymentSchedule = (claim = {}) => {
  const schedule = claim?.paymentSchedule;
  return schedule && typeof schedule === "object" ? schedule : {};
};

export const isRecurringPaymentClaim = (claim = {}) =>
  Boolean(getClaimPaymentSchedule(claim).isRecurring);

export const computeDepositPayableFromInvoice = (
  invoiceTotal,
  depositSignals = {},
) => {
  const depositAmount = normalizeAmount(depositSignals.depositAmount);
  if (depositAmount != null && depositAmount > 0) {
    return invoiceTotal > 0 ? Math.min(depositAmount, invoiceTotal) : depositAmount;
  }
  const depositPercentage = normalizeAmount(depositSignals.depositPercentage);
  if (depositPercentage != null && depositPercentage > 0 && invoiceTotal > 0) {
    return Math.round(((invoiceTotal * depositPercentage) / 100) * 100) / 100;
  }
  return invoiceTotal;
};

export const resolveClaimInvoiceTotal = (claim = {}) => {
  const numeric = resolveClaimNumericAmount(claim);
  // Standard claims: the current claim amount is the payable invoice total.
  // Amendments update numericAmount/totalAmount but used to leave invoiceTotalAmount
  // and OCR totals at the original figure, which blocked matching POPs.
  if (!isDepositPaymentClaim(claim) && numeric > 0) return numeric;

  const stored = normalizeAmount(claim.invoiceTotalAmount);
  if (stored != null && stored > 0) return stored;

  const ocrTotal =
    normalizeAmount(claim.claimDocumentExtraction?.totalAmount) ??
    normalizeAmount(claim.ocrExtracted?.totalAmount);
  if (ocrTotal != null && ocrTotal > 0) return ocrTotal;

  if (!isDepositPaymentClaim(claim)) return numeric;

  const signals = getClaimDepositSignals(claim);
  const depositPayable = computeDepositPayableFromInvoice(numeric, signals);
  if (numeric > depositPayable + AMOUNT_TOLERANCE) return numeric;

  const depositPercentage = normalizeAmount(signals.depositPercentage);
  if (depositPercentage != null && depositPercentage > 0 && numeric > 0) {
    return Math.round((numeric / depositPercentage) * 10000) / 100;
  }

  const settlementTotal = normalizeAmount(claim.paymentSettlement?.invoiceTotal);
  if (settlementTotal != null && settlementTotal > 0) return settlementTotal;

  return numeric;
};

const resolveCashAppliedForPaymentEntry = (entry = {}, claim = {}) => {
  const recorded = normalizeAmount(entry.popAmountApplied);
  let applied = recorded != null && recorded >= 0 ? recorded : 0;
  const popOnProof = normalizeAmount(entry.popValidation?.popAmount);
  if (popOnProof != null && popOnProof > applied + AMOUNT_TOLERANCE) {
    const invoiceTotal = resolveClaimInvoiceTotal(claim);
    applied =
      invoiceTotal > AMOUNT_TOLERANCE
        ? Math.min(popOnProof, invoiceTotal)
        : popOnProof;
  }
  return Math.max(0, applied);
};

const resolveClaimAmountPaidSoFarFromHistory = (claim = {}) => {
  const history = Array.isArray(claim.paymentHistory) ? claim.paymentHistory : [];
  if (!history.length) return null;
  const total = history.reduce(
    (sum, entry) => sum + resolveCashAppliedForPaymentEntry(entry, claim),
    0,
  );
  return Math.round(total * 100) / 100;
};

export const resolveClaimAmountPaidSoFar = (claim = {}) => {
  const settlement = claim.paymentSettlement;
  const recordedPaid = normalizeAmount(settlement?.totalPaid) ?? 0;
  const popAppliedStored = normalizeAmount(settlement?.popAmountApplied);
  let settlementPaid = recordedPaid;
  if (popAppliedStored != null && popAppliedStored > settlementPaid + AMOUNT_TOLERANCE) {
    settlementPaid = popAppliedStored;
  }

  const historyPaid = resolveClaimAmountPaidSoFarFromHistory(claim);
  if (historyPaid == null) return settlementPaid;
  const merged = Math.max(settlementPaid, historyPaid);
  const invoiceTotal = resolveClaimInvoiceTotal(claim);
  if (invoiceTotal > AMOUNT_TOLERANCE) {
    return Math.min(merged, invoiceTotal);
  }
  return merged;
};

export const resolveClaimSupplierCreditOnClaim = (claim = {}) =>
  claim.supplierCreditEntryId || claim.supplierCreditRecorded
    ? 0
    : Math.max(0, normalizeAmount(claim.supplierCreditAmount) || 0);

export const resolveClaimRemainingBalance = (claim = {}) => {
  const invoiceTotal = resolveClaimInvoiceTotal(claim);
  const paid = resolveClaimAmountPaidSoFar(claim);
  return Math.max(0, Math.round((invoiceTotal - paid) * 100) / 100);
};

export const hasDepositBalanceOutstanding = (claim = {}) => {
  if (!isDepositPaymentClaim(claim)) return false;
  return resolveClaimRemainingBalance(claim) > AMOUNT_TOLERANCE;
};

export const hasOutstandingPaymentBalance = (claim = {}) =>
  resolveClaimRemainingBalance(claim) > AMOUNT_TOLERANCE;

export const isClaimAwaitingInitialDepositPayment = (claim = {}) => {
  const status = String(claim?.status || "")
    .trim()
    .toUpperCase();
  if (status !== "APPROVED") return false;
  if (!isDepositPaymentClaim(claim)) return false;
  return resolveClaimAmountPaidSoFar(claim) <= AMOUNT_TOLERANCE;
};

export const normalizeClaimPaymentScope = (claim = {}, scopeOverride = null) => {
  const scope = String(scopeOverride || "")
    .trim()
    .toLowerCase();
  if (scope === "full" || scope === "deposit") return scope;
  if (isClaimAwaitingInitialDepositPayment(claim)) return "deposit";
  return "full";
};

export const resolveClaimPayableAmount = (claim = {}, options = {}) => {
  const status = String(claim?.status || "")
    .trim()
    .toUpperCase();
  // Open balances: explicit PARTIAL_PAYMENT, or legacy deposit PAID with balance left.
  // Do not re-open standard PAID claims from invoice/paid mismatches.
  if (
    status === "PARTIAL_PAYMENT" ||
    (status === "PAID" && hasDepositBalanceOutstanding(claim))
  ) {
    return resolveClaimRemainingBalance(claim);
  }

  const invoiceTotal = resolveClaimInvoiceTotal(claim);
  if (!isDepositPaymentClaim(claim)) {
    return invoiceTotal;
  }

  const paidSoFar = resolveClaimAmountPaidSoFar(claim);
  if (paidSoFar > AMOUNT_TOLERANCE && hasDepositBalanceOutstanding(claim)) {
    return resolveClaimRemainingBalance(claim);
  }

  const paymentScope = normalizeClaimPaymentScope(claim, options.paymentScope);
  if (isClaimAwaitingInitialDepositPayment(claim) && paymentScope === "full") {
    return invoiceTotal;
  }

  return computeDepositPayableFromInvoice(invoiceTotal, getClaimDepositSignals(claim));
};

export const isClaimAwaitingBalancePayment = (claim = {}) => {
  const status = String(claim?.status || "")
    .trim()
    .toUpperCase();
  if (status === "PARTIAL_PAYMENT") return true;
  if (status === "PAID" && hasDepositBalanceOutstanding(claim)) return true;
  return (
    hasDepositBalanceOutstanding(claim) &&
    resolveClaimAmountPaidSoFar(claim) > AMOUNT_TOLERANCE
  );
};

export const isClaimEligibleForPayment = (claim = {}) => {
  if (isCreditNoteClaim(claim)) return false;
  const status = String(claim?.status || "")
    .trim()
    .toUpperCase();
  if (status === "APPROVED" || status === "PARTIAL_PAYMENT") return true;
  // Legacy deposit path only, standard PAID claims must not re-enter the payment queue.
  if (status === "PAID" && hasDepositBalanceOutstanding(claim)) {
    return true;
  }
  return false;
};

/** True when CFO can close the claim without paying the remaining balance. */
export const canCompleteClaimWithoutFurtherPayment = (claim = {}) => {
  if (!isClaimAwaitingBalancePayment(claim)) return false;
  return resolveClaimRemainingBalance(claim) > AMOUNT_TOLERANCE;
};

export const sumClaimPayableAmounts = (claims = [], paymentScopeByClaimId = {}) =>
  (Array.isArray(claims) ? claims : []).reduce((sum, claim) => {
    const claimId = String(claim?.id || "").trim();
    const paymentScope =
      paymentScopeByClaimId[claimId] ?? paymentScopeByClaimId[claim?.id];
    return (
      sum +
      resolveClaimPayableAmount(claim, {
        paymentScope,
      })
    );
  }, 0);

export const getClaimDepositSummaryLabel = (claim = {}) => {
  if (!isDepositPaymentClaim(claim)) return "";
  const signals = getClaimDepositSignals(claim);
  const depositAmount = normalizeAmount(signals.depositAmount);
  const depositPercentage = normalizeAmount(signals.depositPercentage);
  if (depositAmount != null) {
    return `Deposit ${formatCurrency(depositAmount)}`;
  }
  if (depositPercentage != null) {
    return `Deposit ${depositPercentage}%`;
  }
  if (signals.paymentTerms) {
    return `Deposit · ${String(signals.paymentTerms).trim()}`;
  }
  return "Deposit payment";
};

export const getClaimRecurringSummaryLabel = (claim = {}) => {
  if (!isRecurringPaymentClaim(claim)) return "";
  const schedule = getClaimPaymentSchedule(claim);
  const from = String(schedule.dateFrom || "").trim();
  const end = String(schedule.dateEnd || "").trim();
  if (from && end) return `Recurring ${from} → ${end}`;
  if (from) return `Recurring from ${from}`;
  if (end) return `Recurring until ${end}`;
  return "Recurring payment";
};

export const getClaimPaymentTypeChips = (claim = {}) => {
  const chips = [];
  if (isDepositPaymentClaim(claim)) {
    chips.push({
      key: "deposit",
      label: "Deposit",
      title: getClaimDepositSummaryLabel(claim),
      tone: "amber",
    });
  }
  if (isRecurringPaymentClaim(claim)) {
    chips.push({
      key: "recurring",
      label: "Recurring",
      title: getClaimRecurringSummaryLabel(claim),
      tone: "blue",
    });
  }
  if (!chips.length) {
    chips.push({
      key: "standard",
      label: "Standard",
      title: "Full invoice amount payable",
      tone: "neutral",
    });
  }
  return chips;
};

export const getClaimPayablePresentation = (claim = {}) => {
  const status = String(claim?.status || "")
    .trim()
    .toUpperCase();
  const invoiceTotal = resolveClaimInvoiceTotal(claim);
  const payableAmount = resolveClaimPayableAmount(claim);
  const isDeposit = isDepositPaymentClaim(claim);
  const isPartialPayment = isClaimAwaitingBalancePayment(claim);
  const isPaid = status === "PAID";
  const amountPaidSoFar = resolveClaimAmountPaidSoFar(claim);
  const rawTotalPaid = normalizeAmount(claim.paymentSettlement?.totalPaid) ?? amountPaidSoFar;
  const totalPaid =
    claim.supplierCreditRecorded && rawTotalPaid > invoiceTotal
      ? invoiceTotal
      : rawTotalPaid;
  const remainingBalance = resolveClaimRemainingBalance(claim);
  const supplierCreditOnClaim = resolveClaimSupplierCreditOnClaim(claim);
  const depositDueAmount = isDeposit
    ? computeDepositPayableFromInvoice(invoiceTotal, getClaimDepositSignals(claim))
    : null;
  const showInvoiceTotal =
    (isDeposit || isPartialPayment) &&
    Math.abs(payableAmount - invoiceTotal) > AMOUNT_TOLERANCE;

  return {
    invoiceTotal,
    payableAmount,
    isDeposit,
    isPartialPayment,
    isPaid,
    amountPaidSoFar,
    totalPaid,
    remainingBalance,
    supplierCreditOnClaim,
    depositDueAmount,
    isRecurring: isRecurringPaymentClaim(claim),
    showInvoiceTotal,
    payableLabel: formatCurrency(payableAmount),
    invoiceTotalLabel: formatCurrency(invoiceTotal),
    amountPaidSoFarLabel: formatCurrency(amountPaidSoFar),
    totalPaidLabel: formatCurrency(totalPaid),
    remainingBalanceLabel: formatCurrency(remainingBalance),
    supplierCreditOnClaimLabel: formatCurrency(supplierCreditOnClaim),
    depositDueLabel:
      depositDueAmount != null ? formatCurrency(depositDueAmount) : "",
    depositLabel: getClaimDepositSummaryLabel(claim),
    recurringLabel: getClaimRecurringSummaryLabel(claim),
    paymentTypeChips: getClaimPaymentTypeChips(claim),
  };
};
